/**
 * Justesims Hero Search Autocomplete
 *
 * Every destination card is rendered server-side in sections/je-hero.liquid
 * (snippets/je-hero-dest-card.liquid) — a "Popular Destinations" grid (10
 * curated cards) plus an "all destinations" grid covering every
 * country/regional/global product. This mirrors the All Destinations page's
 * proven search (assets/je-all-destinations.js): render everything once,
 * then filter by showing/hiding on a text match, instead of building cards
 * from a JS-parsed JSON blob (an earlier version of this file did that —
 * a single malformed or empty JSON payload silently broke every search
 * result with no visible error, which is much harder to debug than real
 * DOM elements you can inspect directly).
 */
document.addEventListener('DOMContentLoaded', () => {
  const input = document.getElementById('JeHeroSearch');
  // Scoped from the input itself, not a bare document.querySelector — the
  // drawer's own search form (snippets/header-drawer.liquid) uses the same
  // je-hero__search class, and since the header renders before the hero
  // section in the DOM, an unscoped lookup here was actually binding to
  // the drawer's form instead of the hero's own.
  const form = input ? input.closest('form') : null;
  const pill = input ? input.closest('.je-hero__search-pill') : null;
  const dropdown = pill ? pill.querySelector('[data-je-hero-dropdown]') : null;
  if (!input || !form || !pill || !dropdown) return;

  const popularTitle = dropdown.querySelector('[data-je-hero-popular-title]');
  const popularGrid = dropdown.querySelector('[data-je-hero-popular-grid]');
  const allGrid = dropdown.querySelector('[data-je-hero-all-grid]');
  const emptyMsg = dropdown.querySelector('[data-je-hero-empty]');
  const closeBtn = dropdown.querySelector('[data-je-hero-close]');
  const allCards = Array.from(allGrid.querySelectorAll('[data-je-hero-dest]'));

  let activeIndex = -1;
  let visibleCards = [];

  function selectDestination(card) {
    const url = card.getAttribute('data-je-hero-dest-url');
    if (url) window.location.href = url;
  }

  function closeDropdown() {
    dropdown.hidden = true;
    activeIndex = -1;
  }

  function setActiveOption(index) {
    visibleCards.forEach((card, idx) => {
      const isActive = idx === index;
      card.classList.toggle('je-hero__autocomplete-popular-btn--active', isActive);
      card.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) {
        input.setAttribute('aria-activedescendant', card.id);
        const dropdownRect = dropdown.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        if (cardRect.bottom > dropdownRect.bottom) {
          dropdown.scrollTop += cardRect.bottom - dropdownRect.bottom;
        } else if (cardRect.top < dropdownRect.top) {
          dropdown.scrollTop -= dropdownRect.top - cardRect.top;
        }
      }
    });
    activeIndex = index;
  }

  // Shows the curated popular grid (empty query) or filters the full grid
  // to whatever matches the typed text.
  function filterDestinations(query) {
    const q = query.trim().toLowerCase();
    dropdown.hidden = false;

    if (!q) {
      if (popularTitle) popularTitle.hidden = false;
      popularGrid.hidden = false;
      allGrid.hidden = true;
      if (emptyMsg) emptyMsg.hidden = true;
      visibleCards = Array.from(popularGrid.querySelectorAll('[data-je-hero-dest]'));
      visibleCards.forEach((card, idx) => {
        card.id = `je-autocomplete-option-${idx}`;
      });
      setActiveOption(-1);
      return;
    }

    if (popularTitle) popularTitle.hidden = true;
    popularGrid.hidden = true;
    allGrid.hidden = false;

    visibleCards = [];
    allCards.forEach((card) => {
      const name = card.getAttribute('data-je-hero-dest-name').toLowerCase();
      const match = name.includes(q);
      card.hidden = !match;
      if (match) visibleCards.push(card);
    });
    visibleCards.forEach((card, idx) => {
      card.id = `je-autocomplete-option-${idx}`;
    });

    if (emptyMsg) emptyMsg.hidden = visibleCards.length !== 0;
    setActiveOption(-1);
  }

  allCards.forEach((card) => {
    card.addEventListener('click', (e) => {
      e.preventDefault();
      selectDestination(card);
    });
  });
  Array.from(popularGrid.querySelectorAll('[data-je-hero-dest]')).forEach((card) => {
    card.addEventListener('click', (e) => {
      e.preventDefault();
      selectDestination(card);
    });
  });

  // Below 750px, assets/je-search-full.js marks this input readonly and
  // opens a full-screen search overlay instead — skip opening this small
  // inline dropdown underneath it too.
  input.addEventListener('focus', () => {
    if (input.readOnly) return;
    filterDestinations(input.value);
  });
  input.addEventListener('input', () => filterDestinations(input.value));

  // type="search" inputs fire a native 'search' event when the browser's
  // built-in clear (x) button is clicked, separately from the 'input'
  // event above — clearing the field that way should close the dropdown
  // entirely rather than fall back to showing Popular Destinations again.
  input.addEventListener('search', () => {
    if (!input.value.trim()) closeDropdown();
  });

  if (closeBtn) {
    // Just closes the dropdown, not the whole drawer/menu — input.blur()
    // was removed here because it fires Dawn's own onFocusOut listener on
    // the parent <header-drawer> (assets/global.js), which closes the
    // entire mobile menu once focus leaves it with nothing to replace it.
    closeBtn.addEventListener('click', () => closeDropdown());
  }

  document.addEventListener('click', (e) => {
    if (!pill.contains(e.target)) closeDropdown();
  });

  input.addEventListener('keydown', (e) => {
    if (dropdown.hidden) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        filterDestinations(input.value);
        e.preventDefault();
      }
      return;
    }

    if (visibleCards.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveOption((activeIndex + 1) % visibleCards.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveOption(activeIndex <= 0 ? visibleCards.length - 1 : activeIndex - 1);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < visibleCards.length) {
        e.preventDefault();
        selectDestination(visibleCards[activeIndex]);
      } else {
        const exact = visibleCards.find(
          (card) => card.getAttribute('data-je-hero-dest-name').toLowerCase() === input.value.trim().toLowerCase()
        );
        if (exact) {
          e.preventDefault();
          selectDestination(exact);
        }
      }
    } else if (e.key === 'Escape') {
      closeDropdown();
      input.blur();
    }
  });

  // Falls through to the default form submission (the All Destinations
  // page, which reads ?q= on load — see assets/je-all-destinations.js)
  // unless there's an exact match to jump straight to.
  form.addEventListener('submit', (e) => {
    const query = input.value.trim();
    if (!query) {
      e.preventDefault();
      return;
    }
    const exact = allCards.find(
      (card) => card.getAttribute('data-je-hero-dest-name').toLowerCase() === query.toLowerCase()
    );
    if (exact) {
      e.preventDefault();
      selectDestination(exact);
    }
  });
});
